import { useCallback, useEffect, useRef } from 'react'
import { usePageVisibility } from 'react-page-visibility'
import { useDelay } from 'shared/hooks/use-delay'
import { useTimer } from 'shared/hooks/use-timer'
import { useCourseStepRouter } from '../course-step-router/hook'
import { CourseStepEnum } from '../types/course-step-type'
import { useTrackingApi } from './use-tracking-api'

const TRACKING_START_OFFSET = 5
const TRACKING_UPDATE_INTERVAL = 30

export const useCourseTracking = () => {
  const sessionId = useRef<number>()
  const targetId = useRef<number>()
  const targetType = useRef<CourseStepEnum>()
  const { step } = useCourseStepRouter()
  const { startTrackingSession, updateTrackingSession } = useTrackingApi()
  const isVisible = usePageVisibility()

  const updateTracking = useCallback(async () => {
    if (sessionId.current && targetType.current) {
      await updateTrackingSession(sessionId.current, targetType.current)
      restartTimer()
    }
  }, [])

  const updateTimer = useTimer({
    limitSeconds: TRACKING_UPDATE_INTERVAL,
    onTimeOver: updateTracking,
  })

  function restartTimer() {
    updateTimer.restart()
  }

  const initiateTracking = useCallback(async () => {
    if (targetId.current && targetType.current) {
      const session = await startTrackingSession(targetId.current, targetType.current)

      if (!session?.sessionId) {
        return restartTrackingStartDelay()
      }

      sessionId.current = session.sessionId
      updateTimer.start()
    }
  }, [startTrackingSession])

  const delay = useDelay({
    delay: TRACKING_START_OFFSET,
    onDelayOver: initiateTracking,
  })

  function restartTrackingStartDelay() {
    delay.restart()
  }

  const clearOnUserOut = useCallback(async () => {
    updateTimer.reset()
    sessionId.current = undefined
  }, [updateTimer.reset])

  const staleDelay = useDelay({
    delay: TRACKING_UPDATE_INTERVAL,
    onDelayOver: clearOnUserOut,
  })

  useEffect(() => {
    if (step?.id && step?.type) {
      targetId.current = step.id
      targetType.current = step.type
      sessionId.current = undefined
      updateTimer.reset()
      delay.restart()
    }
  }, [step?.id, step?.type])

  useEffect(() => {
    if (!delay.isRunning()) {
      delay.start()
      return
    }

    if (!isVisible) {
      staleDelay.start()
      return
    }

    if (staleDelay.isRunning()) {
      staleDelay.clear()
      return
    }

    if (staleDelay.hasRun) {
      delay.restart()
      sessionId.current = undefined
    }
  }, [
    isVisible,
    staleDelay.start,
    staleDelay.isRunning,
    staleDelay.clear,
    staleDelay.hasRun,
    delay.start,
  ])
}
